import { QueryAggregate } from '@powerportalspro/core';
import { useAuth } from '@powerportalspro/react';
import { MainGrid, type ViewDefinition } from '@powerportalspro/react-fluent';

// Grouping a MainGrid's rows. The accounts are grouped by their industry — a choice column the
// view doesn't show — with each group's total revenue in its header row, under the revenue
// column, and the whole grid's in the totals row. Click a group's header to collapse it to that
// row; the pager pages the records of the groups left open.

const ALL_ACCOUNTS_VIEW_ID = '91732AD4-B4FE-49FF-80CD-72B280EFF088';
const MY_ACCOUNTS_VIEW_ID = '8B1DDAB6-D58F-4D28-81F3-5CDB1973CC50';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const ACCOUNT_COLUMNS = [
  { columnName: 'name', width: 200 },
  { columnName: 'accountnumber', width: 200 },
  { columnName: 'telephone1', width: 200 },
  { columnName: 'address1_city', width: 200 },
  { columnName: 'revenue', width: 200 },
  { columnName: 'primarycontactid', width: 200 },
];

// `ownerFilter` narrows the rows to the user's own; row security already keeps every view to the
// rows the user may read.
function buildAccountsView(id: string, displayName: string, ownerFilter?: string): ViewDefinition {
  return {
    id,
    tableName: 'account',
    displayName,
    fetchXml: `<fetch>
      <entity name="account">
        <attribute name="accountnumber" />
        <attribute name="address1_city" />
        <attribute name="name" />
        <attribute name="primarycontactid" />
        <attribute name="telephone1" />
        <attribute name="revenue" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />${ownerFilter ? `
        <filter type="or">${ownerFilter}</filter>` : ''}
        <order attribute="name" />
      </entity>
    </fetch>`,
    columns: ACCOUNT_COLUMNS,
  };
}

// Each group's revenue in its header row, and every account's in the totals row.
const REVENUE_TOTALS = [{ columnName: 'revenue', aggregates: [{ aggregate: QueryAggregate.Sum, format: 'N0' }] }];

export function MainGridGroupingSample() {
  const auth = useAuth();
  const currentUserId =
    auth.status === 'authenticated' ? auth.user.userId ?? EMPTY_GUID : EMPTY_GUID;

  const mine = `
          <condition attribute="ppp_owningportaluserid" operator="eq" value="${currentUserId}" />
          <condition attribute="ownerid" operator="eq" value="${currentUserId}" />`;
  const customViews: ViewDefinition[] = [
    buildAccountsView(ALL_ACCOUNTS_VIEW_ID, 'Active Accounts'),
    buildAccountsView(MY_ACCOUNTS_VIEW_ID, 'My Accounts', mine),
  ];

  return (
    <MainGrid
      tableName="account"
      minHeight="450px"
      maxHeight="450px"
      groupBy="industrycode"
      columnAggregates={REVENUE_TOTALS}
      customViewDefinitions={customViews}
      viewIds={[ALL_ACCOUNTS_VIEW_ID, MY_ACCOUNTS_VIEW_ID]}
      defaultViewId={ALL_ACCOUNTS_VIEW_ID}
    />
  );
}
